import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../../../../environments/environment';
import {
  GoogleAuthSetupResponse,
  ResetPasswordPayload,
  ResetPasswordResponse,
  VerifyGoogleAuthSetupPayload,
  VerifyGoogleAuthSetupResponse,
  UserProfile,
  VerificationResponse,
  OtpVerificationRequest,
  OtpVerificationResponse,
  ResetGoogleAuthInitiateResponse,
} from '../models/account-security.model';
import { TokenStorageService } from '../../../../../auth/services/token-storage.service';
import { ApiResponse } from '@shared/models/api-response.models';

/**
 * All HTTP calls in this service return the standard
 * `ApiResponse<T>` envelope. Callers read inner payloads via `res.data`.
 */
@Injectable({
  providedIn: 'root',
})
export class AccountSecurityService {
  private readonly apiUrl = environment.userManagementApiUrl;

  constructor(
    private readonly http: HttpClient,
    private readonly tokenStorage: TokenStorageService
  ) {}

  initiateGoogleAuthSetup(): Observable<ApiResponse<GoogleAuthSetupResponse>> {
    return this.http.post<ApiResponse<GoogleAuthSetupResponse>>(
      `${this.apiUrl}/api/auth/v1/g-auth/setup`,
      {}
    );
  }

  verifyGoogleAuthSetup(
    payload: VerifyGoogleAuthSetupPayload
  ): Observable<ApiResponse<VerifyGoogleAuthSetupResponse>> {
    return this.http.post<ApiResponse<VerifyGoogleAuthSetupResponse>>(
      `${this.apiUrl}/api/auth/v1/g-auth/verify-setup`,
      payload
    );
  }

  resetPassword(
    userId: string,
    payload: ResetPasswordPayload
  ): Observable<ApiResponse<ResetPasswordResponse>> {
    return this.http.post<ApiResponse<ResetPasswordResponse>>(
      `${this.apiUrl}/api/users/${userId}/password/change`,
      payload
    );
  }

  initiateResetGoogleAuthSetup(): Observable<
    ApiResponse<ResetGoogleAuthInitiateResponse>
  > {
    return this.http.post<ApiResponse<ResetGoogleAuthInitiateResponse>>(
      `${this.apiUrl}/api/auth/v1/g-auth/reset/initiate`,
      {}
    );
  }

  resetGoogleAuthSetup(
    token: string
  ): Observable<ApiResponse<GoogleAuthSetupResponse>> {
    const headers = this.buildAuthHeadersWithOtpToken(token);
    return this.http.post<ApiResponse<GoogleAuthSetupResponse>>(
      `${this.apiUrl}/api/auth/v1/g-auth/reset`,
      {},
      { headers }
    );
  }

  getUserProfile(): Observable<ApiResponse<UserProfile>> {
    const headers = this.buildAuthHeaders();
    return this.http.get<ApiResponse<UserProfile>>(
      `${this.apiUrl}/api/users/profile`,
      { headers }
    );
  }

  initiateEmailVerification(): Observable<ApiResponse<VerificationResponse>> {
    const headers = this.buildAuthHeaders();
    return this.http.post<ApiResponse<VerificationResponse>>(
      `${this.apiUrl}/api/auth/v1/verify-email`,
      {},
      { headers }
    );
  }

  initiatePhoneVerification(): Observable<ApiResponse<VerificationResponse>> {
    const headers = this.buildAuthHeaders();
    return this.http.post<ApiResponse<VerificationResponse>>(
      `${this.apiUrl}/api/auth/v1/verify-phone`,
      {},
      { headers }
    );
  }

  verifyOtp(
    payload: OtpVerificationRequest,
    otpToken: string
  ): Observable<ApiResponse<OtpVerificationResponse>> {
    const headers = this.buildAuthHeadersWithOtpToken(otpToken);
    return this.http.post<ApiResponse<OtpVerificationResponse>>(
      `${this.apiUrl}/api/auth/v1/otp/verify`,
      payload,
      { headers }
    );
  }

  private buildAuthHeaders(): HttpHeaders {
    const token = this.tokenStorage.getAccessToken();
    const headersConfig: Record<string, string> = {};

    if (token) {
      headersConfig['Authorization'] = `Bearer ${token}`;
    }
    return new HttpHeaders(headersConfig);
  }

  private buildAuthHeadersWithOtpToken(otpToken: string): HttpHeaders {
    const headersConfig: Record<string, string> = {};
    headersConfig['Authorization'] = `Bearer ${otpToken}`;
    return new HttpHeaders(headersConfig);
  }
}
